"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import Link from "next/link";
import API from "@/utils/api"; // ✅ use your Next alias

// -----------------------------
// API helpers
// -----------------------------
const api = {
  list: async (cursor, limit = 20) => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (cursor) params.set("cursor", String(cursor));
    const { data } = await API.get(`/notifications?${params.toString()}`);
    return data; // { items, nextCursor }
  },

  unreadCount: async () => {
    const { data } = await API.get("/notifications/unread-count");
    return data; // { count }
  },

  markRead: async (id) => {
    const { data } = await API.post(`/notifications/${id}/read`);
    return data;
  },

  markAll: async () => {
    const { data } = await API.post("/notifications/read-all");
    return data;
  },

  seen: async () => {
    const { data } = await API.post("/profile/me/seen");
    return data;
  },

  me: async () => {
    const { data } = await API.get("/profile/me");
    return data;
  },

  updatePrefs: async (prefs) => {
    const { data } = await API.patch("/profile/me", { prefs });
    return data;
  },
};

// -----------------------------
// useNotifications hook (badge polling)
// -----------------------------
function useNotificationsBadge() {
  const [unread, setUnread] = useState(0);
  const intervalRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { count } = await api.unreadCount();
      setUnread(count || 0);
    } catch {
      // keep quiet
    }
  };

  useEffect(() => {
    fetchCount();

    const onVis = () => {
      if (document.visibilityState === "visible") fetchCount();
    };
    document.addEventListener("visibilitychange", onVis);

    intervalRef.current = setInterval(fetchCount, 30000);
    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return { unread, setUnread, refresh: fetchCount };
}

// -----------------------------
// Bell + dropdown
// -----------------------------
export function NotificationBell() {
  const { unread, setUnread, refresh } = useNotificationsBadge();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (cursor) => {
    setLoading(true);
    try {
      const data = await api.list(cursor);
      setItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setNextCursor(data.nextCursor);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    api.seen();
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.markRead(id);
      setItems((prev) => prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {}
  };

  return (
    <div className="relative inline-block text-left">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center rounded-full p-2 hover:bg-[#2f2f38]"
        aria-label="Notifications"
        type="button"
      >
        <span className="text-2xl">🔔</span>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.25rem] px-1 text-xs rounded-full bg-primary text-white text-center">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 origin-top-right rounded-2xl border border-border bg-surface shadow-xl">
          <div className="max-h-96 overflow-auto divide-y">
            {items.length === 0 && (
              <div className="p-4 text-sm text-textSecondary">No notifications yet</div>
            )}

            {items.map((n) => {
              const href = n.link || "#";
              const isInternal = href.startsWith("/");

              const content = (
                <div
                  className={`block px-4 py-3 hover:bg-[#2f2f38] ${n.isRead ? "" : "bg-primary/10"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 h-2.5 w-2.5 rounded-full ${n.isRead ? "bg-gray-300" : "bg-primary/100"}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-textPrimary truncate">{n.title}</div>
                      {n.body && <div className="text-sm text-textSecondary line-clamp-2">{n.body}</div>}
                      <div className="mt-1 text-xs text-textSecondary">
                        {new Date(n.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              );

              return isInternal ? (
                <Link key={n._id} href={href} onClick={() => markRead(n._id)}>
                  {content}
                </Link>
              ) : (
                <a
                  key={n._id}
                  href={href}
                  onClick={() => markRead(n._id)}
                  className="block"
                  target={href !== "#" ? "_blank" : undefined}
                  rel={href !== "#" ? "noreferrer" : undefined}
                >
                  {content}
                </a>
              );
            })}
          </div>

          <div className="p-2 flex items-center gap-2">
            {nextCursor && (
              <button
                onClick={() => load(nextCursor)}
                disabled={loading}
                className="flex-1 rounded-xl px-3 py-2 text-sm border border-gray-300 hover:bg-[#2f2f38]"
                type="button"
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
            <button
              onClick={async () => {
                await api.markAll();
                setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
                setUnread(0);
              }}
              className="rounded-xl px-3 py-2 text-sm border border-gray-300 hover:bg-[#2f2f38]"
              type="button"
            >
              Mark all read
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------
// Full page list (/notifications)
// -----------------------------
export function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (cursor) => {
    setLoading(true);
    try {
      const data = await api.list(cursor, 30);
      setItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setNextCursor(data.nextCursor);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.seen();
  }, []);

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Notifications</h1>
        <button
          onClick={async () => {
            await api.markAll();
            setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
          }}
          className="rounded-xl px-3 py-2 text-sm border border-gray-300 hover:bg-[#2f2f38]"
          type="button"
        >
          Mark all read
        </button>
      </div>

      <div className="bg-surface rounded-2xl border border-border divide-y">
        {items.length === 0 && <div className="p-6 text-textSecondary">No notifications yet</div>}
        {items.map((n) => (
          <div key={n._id} className={`p-4 ${n.isRead ? "bg-surface" : "bg-primary/10"}`}>
            {n.link?.startsWith("/") ? (
              <Link href={n.link} className="block">
                <div className="text-sm font-medium text-textPrimary">{n.title}</div>
                {n.body && <div className="text-sm text-textPrimary mt-1">{n.body}</div>}
                <div className="text-xs text-textSecondary mt-2">
                  {new Date(n.createdAt).toLocaleString()}
                </div>
              </Link>
            ) : (
              <a href={n.link || "#"} className="block">
                <div className="text-sm font-medium text-textPrimary">{n.title}</div>
                {n.body && <div className="text-sm text-textPrimary mt-1">{n.body}</div>}
                <div className="text-xs text-textSecondary mt-2">
                  {new Date(n.createdAt).toLocaleString()}
                </div>
              </a>
            )}
          </div>
        ))}
      </div>

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => load(nextCursor)}
            disabled={loading}
            className="rounded-xl px-4 py-2 text-sm border border-gray-300 hover:bg-[#2f2f38]"
            type="button"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

// -----------------------------
// Profile notifications toggle
// -----------------------------
export function ProfileNotifications() {
  const [loading, setLoading] = useState(true);
  const [inApp, setInApp] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .me()
      .then((m) => {
        if (!mounted) return;
        setInApp(!!m?.prefs?.inApp ?? true);
      })
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const onToggle = async (checked) => {
    setSaving(true);
    try {
      await api.updatePrefs({ inApp: checked });
      setInApp(checked);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-textSecondary">Loading…</div>;

  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <h2 className="text-lg font-semibold">Notifications</h2>
      <label className="mt-3 flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={!!inApp}
          onChange={(e) => onToggle(e.target.checked)}
        />
        In-app notifications
      </label>
      {saving && <div className="mt-2 text-xs text-textSecondary">Saving…</div>}
    </div>
  );
}
